// assets/product-custom-property.js
import { Component } from '@theme/component';

/**
 * @typedef {object} ProductCustomPropertyRefs
 * @property {HTMLInputElement | HTMLTextAreaElement} textInput - The text input.
 * @property {HTMLElement} characterCount - The character count element.
 */

/**
 * A custom element that manages product custom properties
 * @extends Component<ProductCustomPropertyRefs>
 */
class ProductCustomProperty extends Component {
  #abortController = new AbortController();
  #rosterNames = [];

  connectedCallback() {
    super.connectedCallback();

    const product = this.closest('product-component');
    const { signal } = this.#abortController;

    if (this.querySelector('[data-design-style-selector]')) {
      this.addEventListener('change', this.#handleDesignStyleChange, { signal });
      product?.addEventListener('change', this.#handleNativeOptionChange, { signal });
      queueMicrotask(this.#syncDesignStyleSelection);
    }

    if (!this.querySelector('[data-pack-validation]')) return;

    const form = this.refs.textInput?.form;

    this.#setupStepThreeExperience();
    this.#setupRosterBuilder();
    form?.addEventListener('submit', this.#handleSubmit, { capture: true, signal });
    product?.addEventListener('change', this.#handleVariantChange, { signal });
    product?.addEventListener('input', this.#handleTripInput, { signal });
    this.addEventListener('change', this.#handleRosterChoice, { signal });
    this.#syncRosterChoice();
    this.#updatePackGuidance();
    this.#updateTripSummary();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#abortController = new AbortController();
  }

  handleInput() {
    this.#updateCharacterCount();
    if (this.querySelector('[data-pack-validation]')) {
      if (this.refs.textInput?.matches('[data-player-names-source]')) this.#handleRosterSourceInput();
      const packSize = this.#getSelectedPackSize();
      const enteredCount = this.refs.textInput instanceof HTMLTextAreaElement
        ? this.refs.textInput.value.split(/\r?\n/).filter((line) => line.trim()).length
        : 0;
      this.#validatePlayerNames(packSize !== null && enteredCount > packSize);
    }
  }

  #handleVariantChange = () => {
    queueMicrotask(() => {
      this.#updatePackGuidance();
      this.#validatePlayerNames(false);
      this.#updateRosterProgress();
      this.#updateTripSummary();
    });
  };

  #handleTripInput = (event) => {
    if (!(event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement)) return;
    if (!event.target.name.startsWith('properties[')) return;
    this.#updateTripSummary();
  };

  #handleRosterChoice = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || !target.matches('[data-roster-option]')) return;
    this.#saveRosterChoice(target.value);
    this.#syncRosterChoice();
    this.#updatePackGuidance();
    this.#updateRosterProgress();
  };

  #setupStepThreeExperience() {
    const product = this.closest('product-component');
    if (!(product instanceof HTMLElement) || product.dataset.stepThreeEnhanced === 'true') return;

    const getComponent = (key) =>
      product.querySelector(`[data-property-key="${key}"]`)?.closest('product-custom-property-component');
    const tripName = getComponent('trip-or-event-name');
    const destination = getComponent('destination-or-course');
    const year = getComponent('year');
    const customNote = getComponent('inside-joke-or-custom-note');
    const neededBy = getComponent('needed-by-date');

    if (!(tripName instanceof HTMLElement) || !(destination instanceof HTMLElement) || !(year instanceof HTMLElement)) return;

    const parent = tripName.parentElement;
    if (!(parent instanceof HTMLElement)) return;

    product.dataset.stepThreeEnhanced = 'true';
    const step = document.createElement('div');
    step.className = 'product-builder__step-three';
    step.dataset.builderStep = '3';
    step.hidden = product.dataset.builderCurrentStep !== '3';
    step.setAttribute('aria-hidden', step.hidden.toString());
    parent.insertBefore(step, tripName);

    const stepHeading = tripName.querySelector('.product-builder__step-heading');
    if (stepHeading) step.append(stepHeading);

    const identity = document.createElement('section');
    identity.className = 'trip-identity';
    identity.setAttribute('aria-label', 'Trip Identity');
    identity.append(tripName, destination, year);
    step.append(identity);

    if (customNote instanceof HTMLElement || neededBy instanceof HTMLElement) {
      const optional = document.createElement('details');
      optional.className = 'optional-trip-details';
      const summary = document.createElement('summary');
      summary.textContent = 'ADD DETAILS TO MAKE IT YOURS';
      const content = document.createElement('div');
      content.className = 'optional-trip-details__content';
      const intro = document.createElement('p');
      intro.className = 'optional-trip-details__intro';
      intro.textContent = 'Add anything else you would like us to consider when creating your proof.';
      content.append(intro);
      if (customNote instanceof HTMLElement) content.append(customNote);
      if (neededBy instanceof HTMLElement) content.append(neededBy);
      optional.append(summary, content);
      step.append(optional);
    }

    const tripSummary = this.querySelector('[data-trip-summary]');
    if (tripSummary instanceof HTMLElement) step.append(tripSummary);

    const roster = document.createElement('section');
    roster.className = 'roster-builder';
    roster.setAttribute('aria-label', 'Build Your Roster');
    roster.append(this);
    step.append(roster);
  }

  #setupRosterBuilder() {
    const source = this.refs.textInput;
    if (!(source instanceof HTMLTextAreaElement)) return;

    this.#restoreRosterChoice();
    this.#rosterNames = source.value.split(/\r?\n/).map((name) => name.trim());
    this.#updateRosterProgress();
  }

  #rosterStorageKey() {
    return `twc-roster-timing:${window.location.pathname}`;
  }

  #saveRosterChoice(value) {
    try {
      window.sessionStorage.setItem(this.#rosterStorageKey(), value);
    } catch {
      // Session storage can be unavailable in privacy-restricted contexts.
    }
  }

  #restoreRosterChoice() {
    try {
      const saved = window.sessionStorage.getItem(this.#rosterStorageKey());
      if (!saved) return;
      const option = Array.from(this.querySelectorAll('[data-roster-option]')).find(
        (input) => input instanceof HTMLInputElement && input.value === saved
      );
      if (option instanceof HTMLInputElement) option.checked = true;
    } catch {
      // Keep the Liquid default when session storage is unavailable.
    }
  }

  #handleRosterSourceInput() {
    const source = this.refs.textInput;
    if (!(source instanceof HTMLTextAreaElement)) return;
    this.#rosterNames = source.value.split(/\r?\n/).map((name) => name.trim());
    this.#updateRosterProgress();
  }

  #updateRosterProgress() {
    const packSize = this.#getSelectedPackSize();
    const text = this.querySelector('[data-roster-progress-text]');
    if (!packSize || !(text instanceof HTMLElement)) return;
    const complete = this.#rosterNames.filter((name) => name?.trim()).length;
    const countProperty = this.querySelector('[data-roster-count-property]');
    if (countProperty instanceof HTMLInputElement) {
      countProperty.value = complete.toString();
      countProperty.disabled = this.#isRosterDeferred();
    }
    text.removeAttribute('data-status');
    if (complete > packSize) {
      const extra = complete - packSize;
      text.textContent = `${complete} names entered for a ${packSize}-player set. Remove ${extra} ${extra === 1 ? 'name' : 'names'}.`;
      text.dataset.status = 'warning';
    } else if (complete === packSize) {
      text.textContent = `${complete} of ${packSize} names added ✓`;
      text.dataset.status = 'complete';
    } else {
      text.textContent = `${complete} of ${packSize} names added`;
    }
  }

  #updateTripSummary() {
    const product = this.closest('product-component');
    const summary = product?.querySelector('[data-trip-summary]');
    if (!(product instanceof HTMLElement) || !(summary instanceof HTMLElement)) return;
    const valueFor = (key) => {
      const input = product.querySelector(`[data-property-key="${key}"] input, [data-property-key="${key}"] textarea`);
      return input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement ? input.value.trim() : '';
    };
    const packSize = this.#getSelectedPackSize();
    const name = summary.querySelector('[data-trip-summary-name]');
    const destination = summary.querySelector('[data-trip-summary-destination]');
    const year = summary.querySelector('[data-trip-summary-year]');
    const size = summary.querySelector('[data-trip-summary-size]');
    if (name) name.textContent = valueFor('trip-or-event-name') || 'Your trip name';
    if (destination) destination.textContent = valueFor('destination-or-course') || 'Destination or course';
    if (year) year.textContent = valueFor('year') || 'Year';
    if (size) size.textContent = packSize ? `${packSize}-player personalized set` : 'Personalized set';
  }

  #handleDesignStyleChange = (event) => {
    const visualOption = event.target;
    if (!(visualOption instanceof HTMLInputElement) || !visualOption.matches('[data-design-style-value]')) return;

    const selector = this.querySelector('[data-design-style-selector]');
    const product = this.closest('product-component');
    const nativeOptionName = selector?.dataset.nativeOptionName?.trim().toLowerCase();
    const nativeOptions = product?.querySelectorAll('variant-picker input[data-option-name]') ?? [];
    const nativeOption = Array.from(nativeOptions).find(
      (option) =>
        option.dataset.optionName?.trim().toLowerCase() === nativeOptionName &&
        option.value === visualOption.dataset.designStyleValue
    );

    if (!(nativeOption instanceof HTMLInputElement)) {
      this.#syncDesignStyleSelection();
      return;
    }

    if (!nativeOption.checked) {
      nativeOption.checked = true;
      nativeOption.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  #handleNativeOptionChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement) || !target.closest('variant-picker')) return;
    queueMicrotask(this.#syncDesignStyleSelection);
  };

  #syncDesignStyleSelection = () => {
    const selector = this.querySelector('[data-design-style-selector]');
    const product = this.closest('product-component');
    const nativeOptionName = selector?.dataset.nativeOptionName?.trim().toLowerCase();
    const selectedNativeOption = Array.from(
      product?.querySelectorAll('variant-picker input[data-option-name]:checked') ?? []
    ).find((option) => option.dataset.optionName?.trim().toLowerCase() === nativeOptionName);

    if (!(selectedNativeOption instanceof HTMLInputElement)) return;

    this.querySelectorAll('[data-design-style-value]').forEach((visualOption) => {
      if (visualOption instanceof HTMLInputElement) {
        visualOption.checked = visualOption.dataset.designStyleValue === selectedNativeOption.value;
      }
    });
  };

  #handleSubmit = (event) => {
    if (this.#validatePlayerNames(true)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.refs.textInput?.focus();
  };

  #getSelectedPackSize() {
    const product = this.closest('product-component');
    const selectedOptions = product?.querySelectorAll('variant-picker input[type="radio"]:checked') ?? [];

    for (const option of selectedOptions) {
      const match = option.value.match(/^(4|8|12)\b/i);
      if (match) return Number.parseInt(match[1], 10);
    }

    return null;
  }

  #updatePackGuidance() {
    const guidance = this.refs.packGuidance;
    if (!(guidance instanceof HTMLElement)) return;

    const baseText = guidance.dataset.baseText ?? '';
    if (this.#isRosterDeferred()) {
      guidance.hidden = true;
      return;
    }

    guidance.hidden = false;
    const packSize = this.#getSelectedPackSize();
    guidance.textContent = packSize ? `${baseText} — ${packSize} names required.` : baseText;
  }

  #isRosterDeferred() {
    return this.querySelector('[data-roster-option="later"]:checked') instanceof HTMLInputElement;
  }

  #syncRosterChoice() {
    const input = this.refs.textInput;
    const entry = this.querySelector('[data-player-names-entry]');
    const later = this.querySelector('[data-roster-later]');
    if (!(input instanceof HTMLTextAreaElement) || !(entry instanceof HTMLElement)) return;

    const isDeferred = this.#isRosterDeferred();
    const submissionProperty = this.querySelector('[data-roster-submission-property]');
    const countProperty = this.querySelector('[data-roster-count-property]');
    entry.hidden = isDeferred;
    if (later instanceof HTMLElement) later.hidden = !isDeferred;
    input.required = !isDeferred && input.dataset.originallyRequired === 'true';
    input.disabled = isDeferred;
    input.toggleAttribute('aria-required', input.required);
    if (submissionProperty instanceof HTMLInputElement) {
      submissionProperty.value = isDeferred ? 'Customer will send roster after checkout' : 'Names provided';
    }
    if (countProperty instanceof HTMLInputElement) {
      countProperty.value = this.#rosterNames.filter((name) => name?.trim()).length.toString();
      countProperty.disabled = isDeferred;
    }

    if (isDeferred) {
      input.setCustomValidity('');
      input.removeAttribute('aria-invalid');
      input.removeAttribute('aria-describedby');
      const error = this.refs.validationError;
      if (error instanceof HTMLElement) {
        error.textContent = '';
        error.hidden = true;
      }
    } else {
      this.#updateRosterProgress();
    }
  }

  #validatePlayerNames(showError) {
    const input = this.refs.textInput;
    const error = this.refs.validationError;
    if (!(input instanceof HTMLTextAreaElement) || !(error instanceof HTMLElement)) return true;

    if (this.#isRosterDeferred()) {
      input.setCustomValidity('');
      input.removeAttribute('aria-invalid');
      error.textContent = '';
      error.hidden = true;
      return true;
    }

    const packSize = this.#getSelectedPackSize();
    const lineCount = input.value.split(/\r?\n/).filter((line) => line.trim().length > 0).length;
    const isValid = packSize === null || lineCount === packSize;
    const remaining = packSize === null ? 0 : Math.max(packSize - lineCount, 0);
    const message = isValid
      ? ''
      : remaining > 0
        ? `Add ${remaining} more player ${remaining === 1 ? 'name' : 'names'} or choose to send your roster after checkout.`
        : `Remove ${lineCount - packSize} extra ${lineCount - packSize === 1 ? 'name' : 'names'} or choose to send your roster after checkout.`;

    input.setCustomValidity(message);
    input.toggleAttribute('aria-invalid', !isValid);

    if (isValid) {
      input.removeAttribute('aria-describedby');
      error.textContent = '';
      error.hidden = true;
    } else {
      input.setAttribute('aria-describedby', error.id);
      error.textContent = showError ? message : '';
      error.hidden = !showError;
      if (showError) {
        input.focus({ preventScroll: true });
        input.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }

    return isValid;
  }

  validateRoster(showError = true) {
    return this.#validatePlayerNames(showError);
  }

  #updateCharacterCount() {
    const { characterCount, textInput } = this.refs;
    const currentLength = textInput.value.length;
    const maxLength = textInput.maxLength;

    const template = characterCount.getAttribute('data-template');
    if (!template) return;

    const updatedText = template.replace('[current]', currentLength.toString()).replace('[max]', maxLength.toString());

    characterCount.textContent = updatedText;
  }
}

customElements.define('product-custom-property-component', ProductCustomProperty);
